/**
 * Is this AMS record an insured or a prospect?
 *
 * Linked records are saved as $$IDF_Name##id$$ tokens for both insureds and
 * prospects (the backend only knows insured vs policy), so the kind is looked
 * up from the AMS — the same insuredandprospectListById call the Insured
 * Details panel makes, whose `type` is "Prospect" for prospects. One request
 * per id per session; callers that already know the kind (an @-mention of a
 * prospect, the AI's prospect_id) seed the cache and skip the request.
 *
 * Known kinds are also kept in sessionStorage, so a page refresh doesn't look
 * every linked chip up again. fetchInsuredRecord shares the same request with
 * other callers that need the full record (e.g. the Command panel's
 * MOMENTUM_IDS resolver), so each id is fetched at most once per session.
 */
import { useEffect, useState } from 'react';

export type InsuredKind = 'insured' | 'prospect';

const STORAGE_KEY = 'ig-insured-kinds';
const CHANGE_EVENT = 'ig-insured-kind-changed';

function loadStoredKinds(): Map<string, InsuredKind> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const obj = raw ? (JSON.parse(raw) as Record<string, InsuredKind>) : {};
    return new Map(Object.entries(obj).filter(([, v]) => v === 'insured' || v === 'prospect'));
  } catch {
    return new Map();
  }
}

const cache = loadStoredKinds();
// Full insured/prospect records — in-flight and recently fetched (this page view).
const recordInFlight = new Map<string, Promise<any | null>>();
const records = new Map<string, any>();

function persistKinds(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(cache)));
  } catch { /* storage full or blocked — the in-memory cache still works */ }
}

export function seedInsuredKind(id: string | null | undefined, kind: InsuredKind): void {
  const key = (id || '').trim();
  if (!key || cache.get(key) === kind) return;
  cache.set(key, kind);
  persistKinds();
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * The AMS insured/prospect record (insuredandprospectListById, first item), or
 * null. One request per id: concurrent and later callers share the result.
 * Seeds the insured/prospect kind as a side effect.
 */
export function fetchInsuredRecord(id: string): Promise<any | null> {
  const key = (id || '').trim();
  if (!key) return Promise.resolve(null);
  if (records.has(key)) return Promise.resolve(records.get(key));
  const pending = recordInFlight.get(key);
  if (pending) return pending;
  const authToken = localStorage.getItem('authToken') || '';
  const amsToken = localStorage.getItem('amsToken') || '';
  const base = process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com';
  const req = fetch(
    `${base}/api/ampnowcerts/insuredandprospectListById?insuredDatabaseId=${encodeURIComponent(key)}&nowCertsToken=${encodeURIComponent(amsToken)}`,
    { headers: { Authorization: `Bearer ${authToken}` } },
  )
    .then((r) => (r.ok ? r.json() : null))
    .then((json) => {
      const item = Array.isArray(json) ? json[0] : json;
      if (!item) return null;
      records.set(key, item);
      seedInsuredKind(key, String(item.type ?? '').toLowerCase() === 'prospect' ? 'prospect' : 'insured');
      return item;
    })
    .catch(() => null)
    .finally(() => { recordInFlight.delete(key); });
  recordInFlight.set(key, req);
  return req;
}

export function getCachedInsuredKind(id: string): InsuredKind | undefined {
  return cache.get(id.trim());
}

function fetchInsuredKind(id: string): Promise<InsuredKind | null> {
  const key = id.trim();
  const known = cache.get(key);
  if (known) return Promise.resolve(known);
  return fetchInsuredRecord(key).then(() => cache.get(key) ?? null);
}

/**
 * Kinds for the given insured/prospect ids. Unknown ids read as 'insured'
 * until the lookup answers (the common case), then re-render.
 */
export function useInsuredKinds(ids: string[]): Record<string, InsuredKind> {
  const [, bump] = useState(0);
  const key = ids.map((i) => i.trim()).filter(Boolean).sort().join('|');

  useEffect(() => {
    const onChange = () => bump((v) => v + 1);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!key) return;
    for (const id of key.split('|')) {
      if (!cache.has(id)) void fetchInsuredKind(id);
    }
  }, [key]);

  const out: Record<string, InsuredKind> = {};
  for (const id of ids) {
    const k = id.trim();
    if (k) out[k] = cache.get(k) ?? 'insured';
  }
  return out;
}
